/**
 * Worklist — company window.
 *
 * Opened from a block header, in the same window component the offer/order
 * detail uses (modules/components/windows), so it can be moved, resized and
 * kept open next to the list.
 *
 * Three parts:
 *   - the relationship in numbers: orders and their total, offers, emails,
 *     calls, chats, since when;
 *   - the recap of what is still open — the very rows the block already
 *     carries, so it costs no extra request;
 *   - the timeline of everything that happened: offers and orders created and
 *     validated, emails in and out, phone calls (live from 3CX), chat
 *     sessions, notes.
 *
 * Numbers and timeline come from one call to
 * GET /v2/sales-inbox/worklist/timeline — the window opens once, so it asks
 * once.
 */
define('salesInboxWorklistPopupView',[
  'require',
  'module',
  'app',
  'marionette',
  'underscore',
  'jquery',
  'salesInboxWorklistRowActions',
  'entities/sales_inbox'
], function (require, Module, App, Marionette, _, $, RowActions) {
  'use strict';

  var TIMELINE_LIMIT = 80;
  // Depth of the closed section when it is opened — the backend caps it too.
  var CLOSED_LIMIT = 100;

  // Label + glyph per backend event type. Unknown types still render, with the
  // raw constant as label — a new backend event never breaks the window.
  // Each kind gets an icon, a tint, and a lane. The lane is what makes the log
  // read as a conversation: what the customer did sits on the left, what we did
  // on the right, and the business milestones run across the middle — they
  // belong to neither side.
  var EVENTS = {
    OFFER_CREATED:   { label: 'Offer created',   icon: 'fa-file-text-o',   cls: 'offer', lane: 'mid' },
    ORDER_CREATED:   { label: 'Order created',   icon: 'fa-cube',          cls: 'order', lane: 'mid' },
    ORDER_VALIDATED: { label: 'Order validated', icon: 'fa-check',         cls: 'order', lane: 'mid' },
    EMAIL_IN:        { label: 'Email received',  icon: 'fa-envelope-o',    cls: 'mail',  lane: 'in' },
    EMAIL_OUT:       { label: 'Email sent',      icon: 'fa-paper-plane-o', cls: 'mail',  lane: 'out' },
    REPLY:           { label: 'Replied',         icon: 'fa-reply',         cls: 'mail',  lane: 'out' },
    CALL_IN:         { label: 'Call received',   icon: 'fa-phone',         cls: 'call',  lane: 'in' },
    CALL_OUT:        { label: 'Call made',       icon: 'fa-phone',         cls: 'call',  lane: 'out' },
    CHAT:            { label: 'Chat',            icon: 'fa-comment-o',     cls: 'chat',  lane: 'in' },
    NOTE:            { label: 'Note',            icon: 'fa-sticky-note-o', cls: 'note',  lane: 'out' }
  };

  function eventMeta(type) {
    return EVENTS[type] ||
      { label: String(type || '').replace(/_/g, ' '), icon: 'fa-circle-o', cls: 'note', lane: 'mid' };
  }

  function formatDate(value) {
    if (!value) { return ''; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    return d.toLocaleString('fr-BE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  function formatShortDate(value) {
    if (!value) { return '—'; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return '—'; }
    return d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  // Day header of the timeline, so a burst of events on one day reads as one
  // block instead of a wall of repeated dates.
  function formatDay(value) {
    if (!value) { return 'Unknown date'; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return 'Unknown date'; }
    return d.toLocaleDateString('fr-BE', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  function dayKey(value) {
    if (!value) { return ''; }
    var d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  function initials(name) {
    if (!name) { return '?'; }
    var parts = $.trim(name).split(/\s+/);
    if (parts.length === 1) { return parts[0].slice(0, 2).toUpperCase(); }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  // Avatar colour: one of ten palette classes, picked by a hash of the name so
  // a company keeps the same colour on every screen. The colours live in the
  // stylesheet — see the note there on why they cannot be computed in CSS.
  function avatarColor(name) {
    var str = String(name || '');
    var hash = 0;
    for (var i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) % 997;
    }
    return 'c' + (hash % 10);
  }

  function gaugeFill(score) {
    return Math.max(6, Math.min(100, Math.round((score || 0) / 30 * 100)));
  }

  function gaugeHeat(score) {
    if (score >= 20) { return 'hot'; }
    if (score >= 10) { return 'warm'; }
    return 'cool';
  }

  function thousands(value) {
    return String(Math.round(Number(value) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function formatAmount(value) {
    if (value === null || value === undefined || value === '') { return ''; }
    return thousands(value) + ' €';
  }

  function formatDuration(seconds) {
    var s = Number(seconds) || 0;
    if (!s) { return ''; }
    var m = Math.floor(s / 60);
    return m ? (m + 'm ' + (s % 60) + 's') : (s + 's');
  }

  var STATUS_LABEL = {
    NEW: 'New',
    AWAITING: 'Awaiting',
    RESOLVED: 'Resolved',
    CLOSED: 'Closed'
  };

  // Same chip as the board's rows: the lifecycle told by a dot. It used to sit
  // in the grey meta line as a raw enum ("buyer@acme.be · AWAITING"), which
  // read as noise rather than as the state of the item.
  function statusChip(item) {
    if (!item.inboxStatus) { return ''; }
    var code = String(item.inboxStatus).toUpperCase();
    var tone = 'st-' + code.toLowerCase().replace(/_/g, '-');
    var label = STATUS_LABEL[code] || code;
    return '<span class="wl-status ' + tone + '">' + _.escape(label) + '</span>';
  }

  Module.exports = Marionette.ItemView.extend({
    className: 'wl-popup',

    template: _.template(
      '<div class="wl-popup-head">' +
      '  <span class="wl-av <%= avatarColor %>"><%= avatar %></span>' +
      '  <span class="wl-popup-who">' +
      '    <span class="wl-popup-title"><%= title %></span>' +
      '    <% if (sub) { %><span class="wl-popup-sub"><%= sub %></span><% } %>' +
      '  </span>' +
      '  <span class="wl-gauge <%= heat %>" style="--p:<%= fill %>" title="<%= priorityReason %>">' +
      '    <i><%= priority %></i>' +
      '  </span>' +
      '  <% if (fileLabel) { %><button type="button" class="js-open-file wl-popup-link"><%= fileLabel %></button><% } %>' +
      '</div>' +
      '<div class="wl-sum js-stats"><div class="wl-popup-state">Loading…</div></div>' +
      '<div class="wl-popup-body">' +
      '  <div class="wl-popup-col wl-popup-open">' +
      '    <h6>Open work<span class="wl-popup-n"><%= items.length %></span></h6>' +
      '    <ul class="wl-popup-items js-items"></ul>' +
      // Folded by default: what is done is context, not the job. The heading
      // still states how much there is, so the fold is a choice, not a hiding.
      '    <div class="wl-popup-closed js-closed is-folded">' +
      '      <h6 class="js-closed-toggle" tabindex="0" role="button" aria-expanded="false">' +
      '        <i class="fa fa-caret-right"></i>Closed work' +
      '        <span class="wl-popup-n js-closed-n"></span></h6>' +
      '      <ul class="wl-popup-items js-closed-items"></ul>' +
      '    </div>' +
      '  </div>' +
      '  <div class="wl-popup-col wl-popup-timeline">' +
      '    <h6>Activity</h6>' +
      '    <div class="js-timeline"><div class="wl-popup-state">Loading…</div></div>' +
      '  </div>' +
      '</div>'
    ),

    ui: {
      stats: '.js-stats',
      items: '.js-items',
      timeline: '.js-timeline',
      closed: '.js-closed',
      closedItems: '.js-closed-items',
      closedCount: '.js-closed-n'
    },

    events: {
      'click .js-open-file': 'onOpenFile',
      'click .wl-popup-item': 'onItemClick',
      'click .wl-ev': 'onEventClick',
      'click .js-closed-toggle': 'onToggleClosed',
      'keydown .js-closed-toggle': 'onToggleClosedKey'
    },

    /** options.customer is one block of the worklist response. */
    initialize: function (options) {
      this.customer = (options && options.customer) || {};
      this.itemsByKey = {};
      this.userViews = [];
      this.closedLoaded = false;
    },

    keyOf: function (item) {
      var key = (item.type || '') + ':' + (item.secId || '');
      this.itemsByKey[key] = item;
      return key;
    },

    /**
     * Mount the shared avatars component on each row: the users assigned to the
     * row, then those assigned to its customer. Same component as the orders
     * board, read-only — assignments are made where the work is.
     */
    mountUsers: function ($scope) {
      var self = this;
      var $rows = $scope.find('.wl-users');
      if (!$rows.length) { return; }
      require(['usersView', 'backbone'], function (UsersView, Backbone) {
        if (self.isDestroyed) { return; }
        $rows.each(function () {
          var $host = $(this);
          var item = self.itemsByKey[$host.attr('data-row')];
          if (!item) { return; }
          var users = item.userpids || [];
          self.mountUserGroup($host.find('.wl-users-own'), UsersView, Backbone,
            _.where(users, { customerLinked: false }));
          self.mountUserGroup($host.find('.wl-users-cust'), UsersView, Backbone,
            _.where(users, { customerLinked: true }));
        });
      });
    },

    mountUserGroup: function ($host, UsersView, Backbone, users) {
      if (!$host.length || !users.length) { return; }
      var view = new UsersView({
        model: new Backbone.Model({ userpids: users }),
        readOnly: true
      });
      view.render();
      $host.append(view.el);
      view.triggerMethod('show');
      this.userViews.push(view);
    },

    destroyUsers: function () {
      _.each(this.userViews, function (view) { view.destroy(); });
      this.userViews = [];
    },

    onDestroy: function () {
      this.destroyUsers();
    },

    serializeData: function () {
      var c = this.customer;
      var title = c.companyName || c.customerName || c.customerEmail || 'Unknown customer';
      var contacts = _.compact(_.map(c.contacts || [], function (contact) {
        return contact.name || contact.email || contact.endUserId;
      }));
      return {
        title: title,
        avatar: initials(title),
        avatarColor: avatarColor(title),
        sub: contacts.join(' · '),
        priority: c.priorityScore || 0,
        fill: gaugeFill(c.priorityScore),
        heat: gaugeHeat(c.priorityScore || 0),
        priorityReason: c.priorityReason || 'No boost',
        fileLabel: c.companySecId ? 'Open company' : (c.customerSecId ? 'Open customer' : ''),
        items: c.items || []
      };
    },

    onShow: function () {
      this.renderItems();
      this.fetchDetail();
    },

    // ---- open work ------------------------------------------------------
    renderItems: function () {
      var items = this.customer.items || [];
      if (!items.length) {
        this.ui.items.html('<li class="wl-popup-state">Nothing open.</li>');
        return;
      }
      this.destroyUsers();
      this.ui.items.html(_.map(items, this.itemHtml, this).join(''));
      this.mountUsers(this.ui.items);
    },

    itemHtml: function (item) {
      var isOffer = item.type === 'OFFER';
      var title = isOffer ?
        (item.title || item.productName || item.purchaseOrderId || '') :
        (item.title || '(no subject)');
      var meta = isOffer ?
        _.compact([
          item.purchaseOrderId,
          item.productName !== title ? item.productName : '',
          item.quantity ? thousands(item.quantity) + ' pcs' : '',
          item.statusName
        ]).join(' · ') :
        (item.senderEmail || '');
      var team = (item.salesAction && item.graphAction) ? 'both' :
        (item.graphAction ? 'graph' : 'sales');
      return '<li class="wl-popup-item"' +
        ' data-type="' + _.escape(item.type || '') + '"' +
        ' data-secid="' + _.escape(item.secId || '') + '"' +
        ' data-poi="' + _.escape(item.purchaseOrderId || '') + '">' +
        '<span class="wl-ic ' + team + '"><i class="fa ' +
        (isOffer ? 'fa-file-text-o' : 'fa-envelope-o') + '"></i></span>' +
        RowActions.dtpIcon(item) +
        '<span class="wl-popup-item-main">' +
        '  <span class="wl-popup-item-title">' +
        '    <span class="wl-popup-item-name">' + _.escape(title) + '</span>' +
        (isOffer ? '' : statusChip(item)) + '</span>' +
        '  <span class="wl-popup-item-meta">' + _.escape(meta) + '</span>' +
        (item.note ? '<span class="wl-note" title="' + _.escape(item.note) + '">' +
          '<i class="fa fa-sticky-note-o"></i>' + _.escape(item.note) + '</span>' : '') +
        '</span>' +
        '<span class="wl-popup-item-amount">' +
        (item.price ? _.escape(formatAmount(item.price)) : '') + '</span>' +
        '<span class="wl-users" data-row="' + _.escape(this.keyOf(item)) + '">' +
        '  <span class="wl-users-own" title="Assigned to this item"></span>' +
        '  <span class="wl-users-cust" title="Assigned to the customer"></span></span>' +
        '<span class="wl-popup-item-date">' + _.escape(formatShortDate(item.date)) + '</span>' +
        '</li>';
    },

    // ---- closed work ----------------------------------------------------
    /**
     * The finished items, drawn with the very row renderer the open ones use —
     * so a closed row keeps its status, language and classification tags and
     * reads as the same object, just done.
     *
     * The rows are built once, on arrival, rather than on first unfold: they
     * are already in hand, and building them here keeps the fold instant.
     */
    renderClosed: function (items) {
      if (!this.ui.closed.length) { return; }
      if (!items.length) {
        // Nothing was ever closed: no heading to offer.
        this.ui.closed.remove();
        return;
      }
      this.ui.closedCount.text(items.length);
      _.each(items, function (item) {
        this.itemsByKey[this.keyOf(item)] = item;
      }, this);
      this.ui.closedItems.html(_.map(items, this.itemHtml, this).join(''));
      this.mountUsers(this.ui.closedItems);
    },

    /**
     * The count arrives with the window; the rows do not. Nothing is fetched
     * until someone actually unfolds the section — a customer with years of
     * closed work costs a COUNT, not a payload, to a window nobody opens.
     */
    showClosedCount: function (count) {
      if (!this.ui.closed.length) { return; }
      if (!count) {
        // Nothing was ever closed: no heading to offer.
        this.ui.closed.remove();
        return;
      }
      this.closedCount = count;
      this.ui.closedCount.text(count);
    },

    onToggleClosed: function () {
      var folded = this.ui.closed.hasClass('is-folded');
      this.ui.closed.toggleClass('is-folded', !folded);
      this.$('.js-closed-toggle').attr('aria-expanded', folded ? 'true' : 'false');
      if (folded) { this.loadClosed(); }
    },

    // Fetched once, on the first unfold: folding back and forth afterwards is
    // a class toggle, not a round trip.
    loadClosed: function () {
      var self = this;
      if (this.closedLoaded) { return; }
      this.closedLoaded = true;

      var c = this.customer;
      var params = { limit: CLOSED_LIMIT };
      if (c.companySecId) { params.companySecId = c.companySecId; }
      if (c.customerSecId) { params.customerSecId = c.customerSecId; }

      this.ui.closedItems.html('<li class="wl-popup-state">Loading…</li>');
      App.request('salesInbox:worklist-closed', params).done(function (items) {
        if (self.isDestroyed) { return; }
        if (!items || !items.length) {
          self.ui.closedItems.html('<li class="wl-popup-state">Nothing closed.</li>');
          return;
        }
        self.renderClosed(items);
      }).fail(function () {
        if (self.isDestroyed) { return; }
        // Let a retry through: the section is not in a loaded state after all.
        self.closedLoaded = false;
        self.ui.closedItems.html('<li class="wl-popup-state wl-popup-state-bad">' +
          'Could not load the closed work.</li>');
      });
    },

    onToggleClosedKey: function (e) {
      if (e.which === 13 || e.which === 32) {
        e.preventDefault();
        this.onToggleClosed();
      }
    },

    // ---- numbers + timeline ---------------------------------------------
    fetchDetail: function () {
      var self = this;
      var c = this.customer;
      var params = { limit: TIMELINE_LIMIT };
      if (c.companySecId) { params.companySecId = c.companySecId; }
      if (c.customerSecId) { params.customerSecId = c.customerSecId; }
      if (!params.companySecId && !params.customerSecId) {
        // An email from a sender that matches no customer has no file to walk
        // back through — the open work above is the whole story.
        this.ui.stats.remove();
        this.ui.closed.remove();
        this.ui.timeline.html('<div class="wl-popup-state">' +
          'This sender has no customer file yet, so there is no history to show.</div>');
        return;
      }
      App.request('salesInbox:worklist-timeline', params).done(function (detail) {
        if (self.isDestroyed) { return; }
        self.renderStats(detail.stats || {});
        self.showClosedCount((detail.stats || {}).closedCount || 0);
        self.renderTimeline(detail.events || []);
      }).fail(function () {
        if (self.isDestroyed) { return; }
        self.ui.stats.remove();
        self.ui.closed.remove();
        self.ui.timeline.html('<div class="wl-popup-state wl-popup-state-bad">' +
          'Could not load the history.</div>');
      });
    },

    // The relationship in numbers: what the customer has spent leads, the rest
    // supports it. Soft tiles rather than a heavy band — this sits above the
    // work, it should not shout over it.
    renderStats: function (stats) {
      var block = this.customer;
      var open = (block.emailCount || 0) + (block.offerCount || 0);
      var figures = [
        { n: thousands(stats.ordersCount || 0), label: 'orders' },
        { n: thousands(stats.offersCount || 0), label: 'offers' },
        { n: thousands(stats.emailsCount || 0), label: 'mails' },
        { n: thousands(stats.callsCount || 0) + (stats.callsCapped ? '+' : ''), label: 'calls' },
        { n: thousands(stats.chatsCount || 0), label: 'chats' },
        { n: thousands(stats.contactsCount || 0), label: block.companyName ? 'contacts' : 'contact' }
      ];
      // The two figures the list card leads with, repeated here so the window
      // stands on its own: how long they have been waiting, and how long since
      // anyone spoke to them. They come from the block, not from the stats
      // call, so they show immediately.
      var waiting = block.waitingDays ?
        'waiting ' + block.waitingDays + ' days' :
        '';
      var lastContact = block.lastContactDate ?
        'last contact ' + formatShortDate(block.lastContactDate) +
          (block.lastContactInbound ? ' (received)' : ' (sent)') :
        'never contacted';
      var meta = _.compact([
        waiting,
        lastContact,
        stats.firstOrderDate ? 'customer since ' + formatShortDate(stats.firstOrderDate) : 'no order yet',
        stats.lastOrderDate ? 'last order ' + formatShortDate(stats.lastOrderDate) : '',
        stats.offersTotal ? formatAmount(stats.offersTotal) + ' quoted' : '',
        open ? open + ' open now' : '',
        'calls cover the last ' + (stats.callsPeriodMonths || 24) + ' months'
      ]).join(' · ');

      this.ui.stats.html(
        '<div class="wl-sum-total">' +
        '  <span class="wl-sum-n">' + _.escape(stats.ordersTotal ? formatAmount(stats.ordersTotal) : '—') + '</span>' +
        '  <span class="wl-sum-l">total ordered</span>' +
        '</div>' +
        '<div class="wl-sum-figs">' +
        _.map(figures, function (f) {
          return '<span class="wl-fig"><b>' + _.escape(f.n) + '</b><i>' + _.escape(f.label) + '</i></span>';
        }).join('') +
        '</div>' +
        '<div class="wl-sum-meta">' + _.escape(meta) + '</div>'
      );
    },

    renderTimeline: function (events) {
      if (!events.length) {
        this.ui.timeline.html('<div class="wl-popup-state">Nothing recorded yet.</div>');
        return;
      }
      var html = '';
      var currentDay = null;
      _.each(events, function (event) {
        var key = dayKey(event.date);
        if (key !== currentDay) {
          currentDay = key;
          html += '<div class="wl-day">' + _.escape(formatDay(event.date)) + '</div>';
        }
        html += this.eventHtml(event);
      }, this);
      this.ui.timeline.html('<div class="wl-evs">' + html + '</div>');
    },

    eventHtml: function (event) {
      var meta = eventMeta(event.type);
      var extras = _.compact([
        event.detail,
        event.amount ? formatAmount(event.amount) : '',
        event.durationSeconds ? formatDuration(event.durationSeconds) : '',
        event.userName ? '— ' + event.userName : ''
      ]).join(' · ');
      // A row is actionable when it points at something openable: an order, a
      // message we know the mailbox and id of, or a call 3CX recorded.
      var openable = !!(event.purchaseOrderId ||
        (event.emailAddressSecId && event.emailMessageId) ||
        (event.callSecId && event.hasRecording));
      return '<div class="wl-ev ev-' + meta.cls + ' lane-' + meta.lane +
        (openable ? ' is-openable' : '') + '"' +
        ' data-poi="' + _.escape(event.purchaseOrderId || '') + '"' +
        ' data-easec="' + _.escape(event.emailAddressSecId || '') + '"' +
        ' data-msgid="' + _.escape(event.emailMessageId || '') + '"' +
        ' data-call="' + _.escape(event.hasRecording ? (event.callSecId || '') : '') + '">' +
        '<span class="wl-ev-ic"><i class="fa ' +
        (event.hasRecording ? 'fa-play' : meta.icon) + '"></i></span>' +
        '<span class="wl-ev-main">' +
        '  <span class="wl-ev-label">' + _.escape(meta.label) + '</span>' +
        '  <span class="wl-ev-title">' + _.escape(event.title || '') + '</span>' +
        '  <span class="wl-ev-meta">' + _.escape(extras) + '</span>' +
        '</span>' +
        '<span class="wl-ev-date">' + _.escape(formatDate(event.date)) + '</span>' +
        '</div>';
    },

    // ---- navigation ------------------------------------------------------
    // Navigating closes the window first: leaving it floating over an
    // unrelated screen would strand it.
    onOpenFile: function () {
      var c = this.customer;
      var route = c.companySecId ? ('companies/' + c.companySecId) :
        (c.customerSecId ? ('customers/' + c.customerSecId) : null);
      if (route) { this.navigate(route); }
    },

    onItemClick: function (e) {
      var $item = $(e.currentTarget);
      if ($item.attr('data-type') === 'OFFER') {
        var poi = $item.attr('data-poi');
        // An offer opens in its own window, like it does everywhere else — no
        // reason to close this one to look at it.
        if (poi) { this.openOrder(poi); }
        return;
      }
      // An email opens in its own window too, so this one stays put underneath
      // — no reason to lose the company you were looking at.
      var secId = $item.attr('data-secid');
      if (!secId) { return; }
      var title = $item.find('.wl-popup-item-title').text();
      require(['salesInboxDetailView'], function (DetailView) {
        DetailView.prototype.open(secId, title);
      });
    },

    onEventClick: function (e) {
      var $event = $(e.currentTarget);
      var poi = $event.attr('data-poi');
      if (poi) {
        this.openOrder(poi);
        return;
      }
      // A recorded call plays in the phone module's own player — the same
      // window the calls screen opens, streamed through the backend because
      // 3CX guards the raw recording URL.
      var callSecId = $event.attr('data-call');
      if (callSecId) {
        this.playCall(callSecId, $event);
        return;
      }
      // An email opens in the shared message viewer — the same window the
      // sales inbox mail search uses.
      var eaSec = $event.attr('data-easec');
      var msgId = $event.attr('data-msgid');
      if (eaSec && msgId) {
        require(['salesInboxMailMessagePopup'], function (MailMessagePopup) {
          MailMessagePopup.open(eaSec, msgId);
        });
      }
    },

    playCall: function (callSecId, $event) {
      var title = _.compact([
        $event.find('.wl-ev-label').text(),
        $event.find('.wl-ev-title').text(),
        $event.find('.wl-ev-date').text()
      ]).join(' | ');
      require(['phoneCallsRecordView', 'entities/phone/calls'], function (RecordView) {
        RecordView.prototype.open({
          url: App.request('calls:record-stream-url', callSecId),
          downloadUrl: App.request('calls:record-stream-url', callSecId, true),
          title: title
        });
      });
    },

    openOrder: function (poi) {
      require(['ordersDetailView'], function (DetailView) {
        if (DetailView && DetailView.prototype && DetailView.prototype.open) {
          DetailView.prototype.open(poi);
        }
      });
    },

    navigate: function (route) {
      if (this.windowView) { this.windowView.close(); }
      App.navigate(route, { trigger: true });
    }
  });
});

